import { useCallback, useEffect, useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type FieldPath } from 'react-hook-form';

import { CountryCode } from '@src/common/auth';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import { getErrorMessage } from '@src/core/store';
import {
  IDENTITY_TYPE,
  isForeignSignupAllowed,
  resolveDialCode,
  resolveForeignIdentityType,
} from '@src/domains/customers';
import { buildSignupPayload } from '@src/features/customers/auth/signup/core/buildSignupPayload';
import { useSignup } from '@src/features/customers/auth/signup/hooks/useSignup';
import { signupErrorsSelector } from '@src/features/customers/auth/store/auth';
import { track } from '@src/services/analytics';
import { appInfoSelector } from '@src/stores/app';

import { isForeignBuyerSelection } from '@src/features/customers/auth/signup/core/resolveSignupFields';
import type { SignupFormValues } from '@src/features/customers/auth/signup/types/signupFormValues';

import { signupSchema } from './signupSchema';

type UseSignupFormParams = {
  initialEmail: string;
  onForgotPassword?: (email: string) => void;
  onAccountCreated?: () => void;
};

export const useSignupForm = (params: UseSignupFormParams) => {
  const { initialEmail, onForgotPassword, onAccountCreated } = params;
  const { i18n } = useTranslate();

  const appInfo = useAppSelector(appInfoSelector);
  const rawSignupErrors = useAppSelector(signupErrorsSelector);

  const { createAccount, clearErrors: clearStoreErrors, pendingSignup } = useSignup();

  const allowForeignBuyers = appInfo?.allowForeignBuyers === true;
  const recaptchaKey = appInfo?.recaptchaKey ?? null;

  const schema = useMemo(
    () => signupSchema(i18n, { allowForeignBuyers, requiresRecaptcha: Boolean(recaptchaKey) }),
    [i18n, allowForeignBuyers, recaptchaKey],
  );

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    getValues,
    formState,
    clearErrors: clearFormErrors,
  } = useForm<SignupFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: {
      email: initialEmail ?? '',
      identityType: IDENTITY_TYPE.CPF,
      firstName: '',
      lastName: '',
      corporateName: '',
      iAmForeign: false,
      countryCode: CountryCode.ARGENTINA,
      foreignIdentityType: resolveForeignIdentityType(CountryCode.ARGENTINA),
      identity: '',
      phone: resolveDialCode(CountryCode.BRAZIL) ?? '',
      zipCode: '',
      password: '',
      passwordConfirmation: '',
      agreesToTerms: false,
      recaptchaToken: null,
    },
  });
  const { errors } = formState;

  const identityType = watch('identityType');
  const iAmForeign = watch('iAmForeign');
  const countryCode = watch('countryCode');

  const isForeign = useMemo(
    () => isForeignBuyerSelection({ identityType, iAmForeign }, allowForeignBuyers),
    [identityType, iAmForeign, allowForeignBuyers],
  );

  const canBeForeign = useMemo(
    () => isForeignSignupAllowed(identityType, allowForeignBuyers),
    [identityType, allowForeignBuyers],
  );

  const isCompany = useMemo(() => identityType === IDENTITY_TYPE.CNPJ, [identityType]);

  // PJ never collects a postal address; foreign buyers get the optional postal code branch.
  const showAddress = useMemo(() => isForeign || identityType === IDENTITY_TYPE.CPF, [isForeign, identityType]);

  const skipInitialStoreSync = useRef(true);

  useEffect(() => {
    clearStoreErrors();
    clearFormErrors();
    track('signupStarted');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset signup state once on mount
  }, []);

  useEffect(() => {
    setValue('email', initialEmail ?? '');
  }, [initialEmail, setValue]);

  useEffect(() => {
    if (skipInitialStoreSync.current) {
      skipInitialStoreSync.current = false;
      return;
    }

    clearFormErrors();

    Object.entries(rawSignupErrors).forEach(([field, rawMessages]) => {
      rawMessages?.forEach((errorKey) => {
        setError(field as FieldPath<SignupFormValues>, {
          type: errorKey,
          message: getErrorMessage(errorKey),
        });
      });
    });
  }, [rawSignupErrors, setError]);

  const applyPhoneDialCode = useCallback(
    (nationality: string) => {
      const dialCode = resolveDialCode(nationality);
      if (dialCode) {
        setValue('phone', dialCode);
      }
    },
    [setValue],
  );

  /**
   * CNPJ signup is Brazil-only, so leaving the flow drops the foreign selection instead of
   * letting a hidden checkbox keep steering validation.
   */
  const onIdentityTypeChange = useCallback(
    (nextIdentityType: SignupFormValues['identityType']) => {
      setValue('identityType', nextIdentityType);
      setValue('identity', '');
      clearFormErrors('identity');

      if (nextIdentityType === IDENTITY_TYPE.CPF) {
        setValue('corporateName', '');
        clearFormErrors('corporateName');
      } else {
        setValue('zipCode', '');
        clearFormErrors('zipCode');
      }

      if (!isForeignSignupAllowed(nextIdentityType, allowForeignBuyers)) {
        setValue('iAmForeign', false);
        applyPhoneDialCode(CountryCode.BRAZIL);
        clearFormErrors(['iAmForeign', 'countryCode', 'foreignIdentityType']);
      }
    },
    [allowForeignBuyers, applyPhoneDialCode, clearFormErrors, setValue],
  );

  /** Nationality drives both the accepted document and the phone country code. */
  const onCountryCodeChange = useCallback(
    (nextCountryCode: string) => {
      setValue('countryCode', nextCountryCode);
      setValue('foreignIdentityType', resolveForeignIdentityType(nextCountryCode));
      applyPhoneDialCode(nextCountryCode);
    },
    [applyPhoneDialCode, setValue],
  );

  const onIamForeignChange = useCallback(
    (checked: boolean) => {
      setValue('iAmForeign', checked);
      setValue('identity', '');
      clearFormErrors('identity');

      if (checked) {
        applyPhoneDialCode(getValues('countryCode') ?? CountryCode.ARGENTINA);
      } else {
        applyPhoneDialCode(CountryCode.BRAZIL);
        clearFormErrors(['countryCode', 'foreignIdentityType']);
      }

      clearFormErrors(['zipCode', 'phone']);
    },
    [applyPhoneDialCode, clearFormErrors, getValues, setValue],
  );

  const onRecoverPassword = useCallback(() => {
    const email = watch('email');

    track('uniqueEmailPasswordRecover');
    onForgotPassword?.(email);
  }, [onForgotPassword, watch]);

  const onSubmit = handleSubmit(async (data) => {
    track('signupSubmit');
    const created = await createAccount(buildSignupPayload(data, { allowForeignBuyers }));

    if (created) {
      onAccountCreated?.();
    }
  });

  return {
    control,
    errors,
    countryCode: countryCode ?? '',
    identityType,
    isForeign,
    isCompany,
    canBeForeign,
    showAddress,
    recaptchaKey,
    loading: pendingSignup,
    getValues,
    setValue,
    onIdentityTypeChange,
    onCountryCodeChange,
    onIamForeignChange,
    onRecoverPassword,
    onSubmit,
  };
};

export { signupSchema } from './signupSchema';
export type { SignupFormValues } from '@src/features/customers/auth/signup/types/signupFormValues';
